import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
} from "react-native";
import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "react-native-calendars";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import Modal from "@components/Modal";
import { colors } from "@constants/theme";
import t from "@constants/localization";
import PressableButton from "@components/PrimaryButton";
import { monthsArray } from "@utils/getFormattedDate";
import ChevronRight from "@assets/svg/ChevronRight";
import ToastMessage from "@components/Toast";

const PATH = "screens/Vacation/components/Calendar";

const Today = dayjs().format("YYYY-MM-DD");

const DateRows = ({ marked }) => {
  const markedDates = () => {
    const vacationsArr = [];
    const vacations = Object.keys(marked).sort();
    for (let i = 0; i < vacations.length; i++) {
      const month = dayjs(vacations[i]).get("month");
      if (!vacationsArr[month]) {
        vacationsArr[month] = [vacations[i]];
      } else {
        vacationsArr[month].push(vacations[i]);
      }
    }
    return vacationsArr;
  };
  const renderDays = (dates) => {
    return dates?.map((day) => (
      <Text
        style={[
          styles.day,
          {
            backgroundColor: dayjs(day).isAfter(Today)
              ? colors.primary
              : colors.blank_grey,
            color: dayjs(day).isAfter(Today) ? "white" : "grey",
          },
        ]}
        key={day}
      >
        {dayjs(day).get("date")}
      </Text>
    ));
  };
  return (
    <ScrollView style={{ maxHeight: 150 }}>
      {markedDates()?.map((dates, i) => {
        if (dates && dates?.length > 0) {
          return (
            <View key={dates}>
              <View style={styles.container} key={dates}>
                <Text style={styles.monthContainer}>
                  {t(`${PATH}/holidays`, { month: monthsArray[i] })}
                </Text>
                <ScrollView
                  contentContainerStyle={{
                    justifyContent: "flex-end",
                    flexGrow: 1,
                  }}
                  horizontal={true}
                  showsHorizontalScrollIndicator={false}
                  style={styles.daysContainer}
                >
                  {renderDays(dates, i)}
                </ScrollView>
              </View>
              <View style={styles.lineSeparator} />
            </View>
          );
        }
      })}
    </ScrollView>
  );
};

const renderArrow = (direction) =>
  direction === "left" ? (
    <View style={{ height: 16, width: 16 }}>
      <ChevronRight
        pathFill={colors.primary}
        style={{
          transform: [{ rotateY: "180deg" }],
        }}
      />
    </View>
  ) : (
    <View style={{ height: 16, width: 16 }}>
      <ChevronRight pathFill={colors.primary} />
    </View>
  );

const renderHeader = (date) => {
  return (
    <View style={styles.monthHeader}>
      <Text style={styles.monthsText}>{dayjs(date).format("MMMM")} </Text>
      <Text style={styles.yearText}>{dayjs(date).format("YYYY")}</Text>
    </View>
  );
};

const handleWarningToast = () => {
  ToastMessage("info", t(`${PATH}/warning`));
};

const Calender = ({
  isVisible,
  closeModel,
  onSubmit,
  selectedDate,
  setSelectedDate,
  newDatesMarked,
  setNewDatesMarked,
}) => {
  const {
    vacation_dates_spent,
    vacation_dates_upcoming,
    max_vacations_in_a_row = 12,
  } = useSelector((state) => state.vacation);

  const [datesMarked, setDatesMarked] = useState([]);
  const [marked, setMarked] = useState({});

  useEffect(() => {
    if (isVisible) {
      sortVacations();
      getAlreadyMarkedDays();
    }
  }, [getAlreadyMarkedDays, isVisible, sortVacations]);

  const getAlreadyMarkedDays = useMemo(
    () =>
      function () {
        const initialDatesMarked = [];
        const CURR_YEAR = new Date().getFullYear();
        const vacations = [...vacation_dates_upcoming, ...vacation_dates_spent];

        for (let i = 0; i < vacations.length; i++) {
          const year = dayjs(vacations[i]).get("year");
          const month = dayjs(vacations[i]).get("month");
          const day = dayjs(vacations[i]).get("date");
          if (year !== CURR_YEAR) continue;
          if (!initialDatesMarked[month]) {
            initialDatesMarked[month] = [day];
          } else {
            initialDatesMarked[month].push(day);
          }
        }
        setDatesMarked(initialDatesMarked);
      },
    [vacation_dates_upcoming, vacation_dates_spent],
  );

  const minDate = () => {
    const nextDay = dayjs().add(2, "day").format("YYYY-MM-DD");
    if (dayjs(nextDay).diff(dayjs(), "minute") > 1680) {
      return nextDay;
    } else {
      return dayjs().add(3, "day").format("YYYY-MM-DD");
    }
  };

  const hasReachedMaxVacationsLimit = [
    ...newDatesMarked.filter(
      (dates) => !vacation_dates_upcoming.includes(dates),
    ),
    ...vacation_dates_spent,
    ...vacation_dates_upcoming,
  ];

  const sortVacations = useMemo(
    () =>
      function () {
        const vacations = [...vacation_dates_upcoming, ...vacation_dates_spent];
        const vacationsSorted = {};
        vacations?.forEach((vacation) => {
          if (
            dayjs(vacation).isBefore(Today) ||
            dayjs(vacation).isSame(Today)
          ) {
            vacationsSorted[vacation] = { status: "SPENT" };
          } else {
            vacationsSorted[vacation] = { status: "UPCOMING" };
          }
        });
        setMarked(vacationsSorted);
      },
    [vacation_dates_spent, vacation_dates_upcoming],
  );

  const markHolidays = (obj) => {
    const currMonth = obj.month - 1,
      currDay = obj.day,
      currDate = obj.dateString;

    const prevDates = [...datesMarked],
      prevMarked = { ...marked };

    if (prevDates[currMonth]?.includes(currDay)) {
      for (let i = 0; i < prevDates[currMonth].length; i++) {
        if (prevDates[currMonth][i] === currDay) {
          prevDates[currMonth].splice(i, 1);
          delete prevMarked[currDate];
          break;
        }
      }
    } else if (hasReachedMaxVacationsLimit.length >= 30) {
      handleWarningToast();
      return;
    } else if (checker(currDate)) {
      if (prevDates[currMonth]) {
        prevDates[currMonth].push(currDay);
      } else {
        prevDates[currMonth] = [currDay];
      }
      prevMarked[currDate] = { status: "UPCOMING" };
    } else return;

    setNewDatesMarked((dates) => {
      if (dates?.includes(currDate)) {
        return dates?.filter((date) => date !== currDate);
      } else {
        return [...dates, currDate];
      }
    });

    setDatesMarked(prevDates);
    setMarked(prevMarked);
  };

  const onCancel = () => {
    closeModel();
    setSelectedDate({});
    setNewDatesMarked([]);
  };

  const onSubmitPress = () => {
    closeModel();
    setNewDatesMarked([]);
    onSubmit();
  };

  const checker = function (currDate) {
    const sortedArr = [
      ...newDatesMarked,
      ...vacation_dates_spent,
      ...vacation_dates_upcoming,
      currDate,
    ].sort((a, b) => {
      return new Date(a) - new Date(b);
    });
    let count = 1;
    for (let i = 1; i < sortedArr.length; i++) {
      if (dayjs(sortedArr[i]).diff(sortedArr[i - 1], "day") === 1) {
        count += 1;
        if (count > max_vacations_in_a_row) break;
      } else {
        count = 1;
      }
    }
    if (count > max_vacations_in_a_row) {
      return false;
    }
    return true;
  };

  const renderDay = ({ date, state, marking }) => {
    const isDisabled = state === "disabled";
    return (
      <TouchableOpacity
        key={date.dateString}
        onPress={() => markHolidays(date)}
        disabled={isDisabled}
        style={[
          styles.dayContainer,
          {
            backgroundColor:
              date?.dateString === Today
                ? marking?.status === "SPENT"
                  ? colors.extraLightBlack
                  : colors.white
                : marking?.status === "UPCOMING"
                  ? colors.primary
                  : marking?.status === "SPENT"
                    ? colors.lighterBlack
                    : colors.white,
            borderColor: "rgba(0, 0, 0, 0.8)",
            borderWidth: date?.dateString === Today ? 1 : 0,
          },
        ]}
      >
        <Text
          style={{
            textAlign: "center",
            color:
              date?.dateString === Today
                ? marking?.status === "SPENT"
                  ? colors.grey
                  : "grey"
                : marking?.status === "UPCOMING"
                  ? colors.white
                  : state === "disabled"
                    ? date?.dateString === Today
                      ? "black"
                      : "grey"
                    : "black",
            fontSize: 12,
            lineHeight: 18,
            fontFamily:
              marking?.status === "UPCOMING"
                ? "NotoSansDevanagari-SemiBold"
                : "NotoSansDevanagari-Regular",
          }}
        >
          {date.day}
        </Text>
      </TouchableOpacity>
    );
  };

  const isSubmitButtonDisabled = selectedDate?.dates
    ? newDatesMarked === selectedDate?.dates
    : newDatesMarked.length === 0;

  const currentDate = () => {
    if (selectedDate && selectedDate.dates) {
      return selectedDate?.dates[0];
    } else {
      return Today;
    }
  };

  const showSubmitButton =
    Object.keys(marked).length !== 0 || newDatesMarked.length !== 0;

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={onCancel}
    >
      <View style={styles.modal}>
        <View style={styles.calendarContainer}>
          <Calendar
            style={styles.calenderStyle}
            theme={calenderTheme}
            markedDates={marked}
            minDate={minDate()}
            hideExtraDays={true}
            renderArrow={renderArrow}
            renderHeader={renderHeader}
            dayComponent={renderDay}
            current={currentDate()}
          />
          {!showSubmitButton && (
            <PressableButton
              text={t(`${PATH}/cancel`)}
              buttonStyle={styles.cancelBtn}
              textStyle={styles.cancelBtnText}
              buttonColor={colors.white}
              onPress={onCancel}
            />
          )}
        </View>
        {showSubmitButton && (
          <View style={styles.parent}>
            <DateRows datesMarked={datesMarked} marked={marked} />
            <View style={styles.buttonContainer}>
              <PressableButton
                text={t(`${PATH}/cancel`)}
                buttonStyle={styles.button}
                textStyle={styles.cancelBtnText}
                buttonColor={colors.white}
                onPress={onCancel}
              />
              <PressableButton
                text={t(`${PATH}/submitIt`)}
                buttonStyle={styles.button}
                textStyle={styles.buttonText}
                onPress={onSubmitPress}
                disabled={isSubmitButtonDisabled}
              />
            </View>
          </View>
        )}
      </View>
    </Modal>
  );
};

export default Calender;

const calenderTheme = {
  dotColor: colors.primary,
  arrowColor: colors.primary,
  selectedDayBackgroundColor: colors.primary,
  textDayFontWeight: "300",
  todayTextColor: colors.lightBlack,
  todayBackgroundColor: colors.lighterBlack,
  textMonthFontWeight: "100",
  "stylesheet.calendar.header": {
    dayHeader: {
      color: colors.black,
      fontFamily: "NotoSansDevanagari-Bold",
      fontSize: 12,
      lineHeight: 18,
    },
  },
};

const styles = StyleSheet.create({
  modal: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.lightBlack,
  },
  calendarContainer: {
    backgroundColor: "white",
    paddingBottom: 6,
    borderRadius: 6,
  },
  calenderStyle: {
    width: Dimensions.get("window").width - 40,
    marginTop: 6,
    marginBottom: 10,
    borderRadius: 6,
  },
  parent: {
    backgroundColor: colors.white,
    width: Dimensions.get("window").width - 40,
    marginVertical: 5,
    borderRadius: 6,
  },
  monthHeader: {
    alignItems: "center",
    flexDirection: "row",
  },
  monthsText: {
    fontFamily: "NotoSansDevanagari-SemiBold",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
    color: colors.black,
  },
  yearText: {
    fontFamily: "NotoSansDevanagari-Regular",
    fontSize: 14,
    fontWeight: "400",
    textAlign: "center",
    color: colors.black,
  },
  container: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 10,
  },
  monthContainer: {
    marginLeft: 12,
    fontFamily: "NotoSansDevanagari-Regular",
    fontSize: 14,
    color: colors.mediumBlack,
  },
  daysContainer: {
    marginLeft: 5,
    flexDirection: "row",
  },
  day: {
    backgroundColor: colors.primary,
    color: "grey",
    marginRight: 7,
    width: 24,
    height: 24,
    borderRadius: 12,
    textAlign: "center",
    padding: 1,
    fontFamily: "NotoSansDevanagari-SemiBold",
    fontSize: 12,
    fontWeight: "600",
    lineHeight: 20,
  },
  lineSeparator: {
    height: 1,
    width: "100%",
    backgroundColor: colors.blank_grey,
  },
  buttonContainer: {
    alignItems: "center",
    marginVertical: 10,
    width: "100%",
    paddingHorizontal: 10,
    flexDirection: "row",
    justifyContent: "space-around",
  },
  button: {
    paddingVertical: 2,
    minWidth: "45%",
    borderRadius: 6,
    height: 36,
  },
  buttonText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 14,
    // lineHeight: 28,
  },
  cancelBtn: {
    alignSelf: "flex-end",
    minWidth: "30%",
    paddingHorizontal: 0,
  },
  cancelBtnText: {
    fontFamily: "NotoSansDevanagari-SemiBold",
    color: colors.lightBlack,
    fontSize: 16,
  },
  dayContainer: {
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 14,
    height: 28,
    width: 28,
    borderColor: colors.lightMediumBlack,
  },
});
